/**
 * Post API functions
 * Uses publicAxios for unauthenticated requests
 * Authenticated requests require passing the axios instance from useAuthenticatedAxios
 */

import publicAxios from './publicAxios';

/**
 * Get posts with optional filters (public)
 * @param {object} filters - Optional filters { communityId, authorId, postType, status }
 */
export const getPosts = async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.communityId) params.append('communityId', filters.communityId);
    if (filters.authorId) params.append('authorId', filters.authorId);
    if (filters.postType) params.append('postType', filters.postType);
    if (filters.status) params.append('status', filters.status);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.search) params.append('search', filters.search);

    const response = await publicAxios.get('/api/posts', { params });
    return response.data;
};

/**
 * Get a single post by ID (public)
 * @param {string} id - Post ID
 */
export const getPost = async (id) => {
    const response = await publicAxios.get(
        `/api/posts/${encodeURIComponent(id)}`
    );
    return response.data;
};

/**
 * Create a new post (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {object} postData - Post data { communityId, postType, title, content }
 * @param {File} [imageFile] - Optional image file to upload
 */
export const createPost = async (axiosInstance, postData, imageFile) => {
    if (imageFile) {
        const formData = new FormData();
        formData.append('communityId', postData.communityId);
        formData.append('postType', postData.postType || 'discussion');
        formData.append('title', postData.title || '');
        formData.append('content', postData.content);
        formData.append('image', imageFile);

        // Include resource fields if present
        if (postData.resourceUrl) {
            formData.append('resourceUrl', postData.resourceUrl);
        }
        if (postData.resourceType) {
            formData.append('resourceType', postData.resourceType);
        }

        // Include event fields if present
        if (postData.eventDate) {
            formData.append('eventDate', postData.eventDate);
        }
        if (postData.eventEndDate) {
            formData.append('eventEndDate', postData.eventEndDate);
        }
        if (postData.eventLocation) {
            formData.append('eventLocation', postData.eventLocation);
        }
        if (postData.eventRegistrationUrl) {
            formData.append(
                'eventRegistrationUrl',
                postData.eventRegistrationUrl
            );
        }

        const response = await axiosInstance.post('/api/posts', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    }

    const response = await axiosInstance.post('/api/posts', postData);
    return response.data;
};

/**
 * Update a post (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Post ID
 * @param {object} updates - Fields to update
 */
export const updatePost = async (axiosInstance, id, updates) => {
    const response = await axiosInstance.put(`/api/posts/${id}`, updates);
    return response.data;
};

/**
 * Delete a post (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Post ID
 */
export const deletePost = async (axiosInstance, id) => {
    const response = await axiosInstance.delete(`/api/posts/${id}`);
    return response.data;
};

/**
 * Pin or unpin a post (authenticated, moderator/admin only)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Post ID
 * @param {boolean} pinned - true to pin, false to unpin
 */
export const pinPost = async (axiosInstance, id, pinned) => {
    const response = await axiosInstance.put(
        `/api/posts/${encodeURIComponent(id)}/pin`,
        { pinned }
    );
    return response.data;
};
